// Robust NCAA school-name matcher for stat / player imports.
// Handles pasted strings like  Northeastern Univ."A"  (the "A"/"C" is an
// Alternate-Captain / Captain designation that must be stripped) and common
// abbreviations (Univ. / University / College etc.).
//
// Admin-confirmed mappings are remembered in localStorage so the SAME pasted
// string auto-matches on every future import.
import { ALL_NCAA_SCHOOLS } from "./ncaaData";

const ALIAS_KEY = "rosterly_ncaa_school_aliases";

// Tokens that carry no identity and should be ignored when comparing names.
// NOTE: "college" is intentionally NOT here — it is part of real names
// (e.g. "Boston College").
const STOP = new Set(["univ", "university", "of", "the", "u"]);

const _aliasKey = (raw) => String(raw || "").trim().toLowerCase();

export function getSchoolAliases() {
    try {
        return JSON.parse(localStorage.getItem(ALIAS_KEY) || "{}");
    } catch {
        return {};
    }
}

export function saveSchoolAlias(raw, school) {
    const k = _aliasKey(raw);
    if (!k || !school) return;
    const map = getSchoolAliases();
    map[k] = school;
    try { localStorage.setItem(ALIAS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

// Lowercase, drop quotes/punctuation, strip trailing captain markers (A/C),
// then drop stop-words — returning a normalised comparison string.
function normalize(str) {
    let toks = String(str || "")
        .replace(/["'`’]/g, " ")
        .replace(/[().,]/g, " ")
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);
    // Strip trailing standalone captain designations: ... "A" / "C".
    while (toks.length && (toks[toks.length - 1] === "a" || toks[toks.length - 1] === "c")) {
        toks.pop();
    }
    return toks.filter((t) => !STOP.has(t)).join(" ");
}

const _canon = ALL_NCAA_SCHOOLS.map(({ school }) => ({ school, norm: normalize(school) }));

// Returns the canonical NCAA school name, or null when no confident match.
export function matchNcaaSchool(raw) {
    const v = String(raw || "").trim();
    if (!v) return null;
    // 1) Admin-remembered alias for this exact pasted string.
    const alias = getSchoolAliases()[_aliasKey(v)];
    if (alias) return alias;
    // 2) Already a canonical name?
    const direct = ALL_NCAA_SCHOOLS.find((s) => s.school.toLowerCase() === v.toLowerCase());
    if (direct) return direct.school;
    const cleaned = normalize(v);
    if (!cleaned) return null;
    // 3) Exact normalised match.
    const exact = _canon.find((c) => c.norm === cleaned);
    if (exact) return exact.school;
    // 4) Containment match — longest canonical name wins to avoid
    //    "Minnesota" swallowing "Minnesota State", etc.
    let best = null, bestLen = 0;
    for (const c of _canon) {
        if (!c.norm) continue;
        if (cleaned.includes(c.norm) || c.norm.includes(cleaned)) {
            if (c.norm.length > bestLen) { best = c.school; bestLen = c.norm.length; }
        }
    }
    return best;
}

export const isKnownNcaaSchool = (name) =>
    ALL_NCAA_SCHOOLS.some((s) => s.school === name);
